import React from 'react';
import { Calendar, MapPin, Users, Phone, Mail, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import SocialShareButtons from '../jamboree/SocialShareButtons';
import JamboreeQR from '../jamboree/JamboreeQR';
import { useUpcomingEvent } from '../../hooks/useUpcomingEvent';

const SECTION_STYLE = {
  background: 'linear-gradient(135deg, #050505 0%, #1a1300 50%, #050505 100%)',
  padding: '4rem 1rem',
};

const GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '2.5rem',
  alignItems: 'center',
  maxWidth: '1100px',
  margin: '0 auto',
};

const FLYER_STYLE = {
  display: 'block',
  borderRadius: '20px',
  overflow: 'hidden',
  boxShadow: '0 20px 60px rgba(255, 215, 0, 0.18)',
};

const PILL_STYLE = {
  display: 'inline-block',
  background: '#FFD700',
  color: '#000',
  padding: '0.35rem 0.95rem',
  borderRadius: '999px',
  fontWeight: 800,
  fontSize: '0.78rem',
  letterSpacing: '1px',
  marginBottom: '1rem',
};

const HEADING_STYLE = {
  fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
  color: '#FFD700',
  margin: '0 0 0.25rem 0',
  lineHeight: 1.15,
};

const SUBTITLE_STYLE = {
  fontSize: '1.05rem',
  color: '#f5f5f5',
  margin: '0 0 0.5rem 0',
  fontWeight: 600,
};

const ICON_STYLE = { color: '#FFD700', flexShrink: 0 };

const InfoRow = ({ icon: Icon, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: '#fff' }}>
    <Icon size={20} style={ICON_STYLE} />
    <span>{children}</span>
  </div>
);

const FEATURES_GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: '0.6rem',
  margin: '1.25rem 0 1.5rem',
};

const FEATURE_CARD_STYLE = {
  background: 'linear-gradient(145deg, rgba(255,215,0,0.14) 0%, rgba(255,215,0,0.04) 100%)',
  border: '1px solid rgba(255, 215, 0, 0.35)',
  borderRadius: '12px',
  padding: '0.75rem 0.85rem',
  textAlign: 'center',
};

const FeatureBadge = ({ emoji, title, subtitle }) => (
  <div style={FEATURE_CARD_STYLE}>
    <div style={{ fontSize: '1.5rem', marginBottom: '0.15rem' }} aria-hidden>{emoji}</div>
    <div style={{ color: '#FFD700', fontWeight: 800, fontSize: '0.82rem', letterSpacing: '0.5px' }}>
      {title}
    </div>
    <div style={{ color: '#d4d4d4', fontSize: '0.72rem', marginTop: '0.15rem' }}>
      {subtitle}
    </div>
  </div>
);

const TAGLINE_BANNER_STYLE = {
  display: 'inline-block',
  background: 'linear-gradient(90deg, #FFD700 0%, #FFC800 50%, #FFD700 100%)',
  color: '#000',
  padding: '0.5rem 1.1rem',
  borderRadius: '999px',
  fontWeight: 900,
  fontSize: '0.78rem',
  letterSpacing: '2.5px',
  boxShadow: '0 6px 20px rgba(255, 215, 0, 0.35)',
  marginBottom: '1rem',
};

const UpcomingEventSection = ({ showShare = false, hideFlyerImage = true }) => {
  const { event: upcomingEvent } = useUpcomingEvent();
  const hasContacts = Array.isArray(upcomingEvent.contacts) && upcomingEvent.contacts.length > 0;
  const hasTickets = Boolean(upcomingEvent.ticketUrl);
  const hasTeamsList = Array.isArray(upcomingEvent.teamsCommitted) && upcomingEvent.teamsCommitted.length > 0;

  return (
    <section
      className="upcoming-event-section"
      data-testid="upcoming-event-section"
      style={SECTION_STYLE}
    >
      <div className="container">
        <div style={hideFlyerImage ? { maxWidth: '900px', margin: '0 auto' } : GRID_STYLE}>
          {/* Flyer image — hidden on /jamboree where dynamic flyer is shown above */}
          {!hideFlyerImage && (hasTickets ? (
            <a
              href={upcomingEvent.ticketUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="upcoming-event-flyer-link"
              style={FLYER_STYLE}
            >
              <img
                src={upcomingEvent.flyerImage}
                alt={`${upcomingEvent.title} flyer`}
                style={{ width: '100%', height: 'auto', display: 'block' }}
                data-testid="upcoming-event-flyer-image"
              />
            </a>
          ) : (
            <div style={FLYER_STYLE} data-testid="upcoming-event-flyer">
              <img
                src={upcomingEvent.flyerImage}
                alt={`${upcomingEvent.title} flyer`}
                style={{ width: '100%', height: 'auto', display: 'block' }}
                data-testid="upcoming-event-flyer-image"
              />
            </div>
          ))}

          {/* Details */}
          <div style={{ color: '#fff' }}>
            <span style={PILL_STYLE}>🏆 SIGNATURE EVENT</span>
            <h2 style={HEADING_STYLE}>{upcomingEvent.title}</h2>
            {upcomingEvent.subtitle && (
              <p style={SUBTITLE_STYLE}>{upcomingEvent.subtitle}</p>
            )}
            {upcomingEvent.tagline && (
              <div style={TAGLINE_BANNER_STYLE} data-testid="upcoming-event-tagline">
                {upcomingEvent.tagline}
              </div>
            )}
            {upcomingEvent.hook && (
              <p style={{ color: '#FFD700', fontStyle: 'italic', margin: '0 0 1rem 0', fontWeight: 700, fontSize: '1rem' }}>
                {upcomingEvent.hook}
              </p>
            )}

            {Array.isArray(upcomingEvent.features) && upcomingEvent.features.length > 0 && (
              <div style={FEATURES_GRID_STYLE} data-testid="upcoming-event-features">
                {upcomingEvent.features.map((f) => (
                  <FeatureBadge key={f.title} emoji={f.emoji} title={f.title} subtitle={f.subtitle} />
                ))}
              </div>
            )}

            <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <InfoRow icon={Calendar}>
                <strong>{upcomingEvent.date}</strong>
                {upcomingEvent.time && <> &nbsp;•&nbsp; {upcomingEvent.time}</>}
              </InfoRow>
              <InfoRow icon={MapPin}>
                <strong>{upcomingEvent.venue}</strong>
                {upcomingEvent.city && <> &nbsp;•&nbsp; {upcomingEvent.city}</>}
              </InfoRow>
              {upcomingEvent.ageGroups && (
                <InfoRow icon={Users}>
                  <strong>Age Groups:</strong> {upcomingEvent.ageGroups}
                </InfoRow>
              )}
            </div>

            {hasTeamsList && (
              <div style={{
                background: 'rgba(255, 215, 0, 0.08)',
                border: '1px solid rgba(255, 215, 0, 0.25)',
                borderRadius: '10px',
                padding: '0.85rem 1rem',
                marginBottom: '1.5rem',
              }}>
                <p style={{ color: '#FFD700', fontWeight: 700, margin: '0 0 0.4rem 0', fontSize: '0.85rem', letterSpacing: '0.5px' }}>
                  🏟️ REPRESENTED TEAMS
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }} data-testid="upcoming-event-teams-list">
                  {upcomingEvent.teamsCommitted.map((team) => (
                    <span
                      key={team}
                      style={{
                        background: 'rgba(0,0,0,0.5)',
                        border: '1px solid rgba(255,215,0,0.4)',
                        borderRadius: '999px',
                        padding: '0.3rem 0.7rem',
                        fontSize: '0.82rem',
                        color: '#fff',
                        fontWeight: 600,
                      }}
                    >
                      {team}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <p style={{ color: '#d4d4d4', lineHeight: 1.6, marginBottom: '1.75rem' }}>
              {upcomingEvent.description}
            </p>

            {/* CTAs: tickets if available, otherwise contact buttons */}
            {hasTickets && (
              <a
                href={upcomingEvent.ticketUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="upcoming-event-tickets-btn"
                style={{ textDecoration: 'none' }}
              >
                <Button className="btn-primary btn-large" style={{ fontWeight: 700 }}>
                  Get Tickets <ChevronRight size={20} />
                </Button>
              </a>
            )}

            {hasContacts && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }} data-testid="upcoming-event-contacts">
                {upcomingEvent.contacts.map((c) => (
                  <a
                    key={c.phone}
                    href={`tel:${c.phone.replace(/[^\d+]/g, '')}`}
                    data-testid={`upcoming-event-contact-${c.name.replace(/\s+/g, '-').toLowerCase()}`}
                    style={{ textDecoration: 'none' }}
                  >
                    <Button className="btn-primary" style={{ fontWeight: 700 }}>
                      <Phone size={18} />
                      &nbsp;{c.name} — {c.phone}
                    </Button>
                  </a>
                ))}
                {upcomingEvent.email && (
                  <a
                    href={`mailto:${upcomingEvent.email}?subject=${encodeURIComponent('Panthers Classic Jamboree — Team Registration')}`}
                    data-testid="upcoming-event-email-btn"
                    style={{ textDecoration: 'none' }}
                  >
                    <Button
                      className="btn-secondary"
                      style={{
                        fontWeight: 700,
                        background: 'transparent',
                        border: '1px solid rgba(255,215,0,0.5)',
                        color: '#FFD700',
                      }}
                    >
                      <Mail size={18} />
                      &nbsp;Email Us
                    </Button>
                  </a>
                )}
              </div>
            )}

            {showShare && (
              <div style={{ marginTop: '1.5rem' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    flexWrap: 'wrap',
                    padding: '1rem',
                    background: 'rgba(255, 215, 0, 0.06)',
                    border: '1px solid rgba(255, 215, 0, 0.28)',
                    borderRadius: '12px',
                  }}
                  data-testid="football-qr-block"
                >
                  <JamboreeQR size={96} caption="SCAN · SHARE" />
                  <div style={{ flex: '1 1 220px', color: '#f5f5f5', fontSize: '0.92rem', lineHeight: 1.45 }}>
                    <strong style={{ color: '#FFD700' }}>Print this flyer or scan the code</strong>
                    <br />
                    Coaches & parents can scan to open the Jamboree page on their phone for
                    full details, coach contacts, and quick registration.
                  </div>
                </div>
                <SocialShareButtons
                  shareTitle="Randallstown Panthers Classic 2026 Jamboree"
                  testIdPrefix="football-share"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default UpcomingEventSection;
